<article class="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
  <p class="text-sm font-medium tracking-wide text-tan uppercase">Legals, rules &amp; privacy</p>
  <h1 class="mt-2 font-display text-4xl font-semibold text-ink italic sm:text-5xl">Terms of Use</h1>
  <p class="mt-6 text-base leading-relaxed text-ink/80">
    Welcome to Galopa, the peer-to-peer marketplace for second-hand saddles. By registering an
    account or using our platform, you agree to these Terms of Use.
  </p>

  <div class="prose-legal mt-12 space-y-10 text-ink">
    <section>
      <h2 class="font-display text-2xl font-semibold text-ink">1. Account Registration and Verification</h2>
      <p class="mt-3 text-base leading-relaxed text-ink/80">
        To maintain a safe marketplace, all users must register for an account.
      </p>
      <ul class="mt-4 list-disc space-y-2 pl-5 text-base leading-relaxed text-ink/80">
        <li>
          Payments and identity verification (KYC) are securely handled by our third-party provider,
          Stripe (via Stripe Connect).
        </li>
        <li>You must provide accurate and verifiable information during registration.</li>
        <li>
          Galopa reserves the right to suspend accounts that fail KYC verification or engage in
          fraudulent activity.
        </li>
      </ul>
    </section>

    <section>
      <h2 class="font-display text-2xl font-semibold text-ink">2. Platform Fees</h2>
      <p class="mt-3 text-base leading-relaxed text-ink/80">
        Galopa charges fees to maintain the platform, hold funds securely during the inspection
        window, and provide buyer/seller protections.
      </p>
      <div class="mt-6 overflow-x-auto">
        <table class="w-full min-w-[36rem] border-collapse text-left text-sm">
          <thead>
            <tr class="border-b border-line">
              <th class="py-3 pr-4 font-semibold text-ink">Fee Type</th>
              <th class="py-3 pr-4 font-semibold text-ink">Amount</th>
              <th class="py-3 pr-4 font-semibold text-ink">Paid By</th>
              <th class="py-3 font-semibold text-ink">Description</th>
            </tr>
          </thead>
          <tbody class="text-ink/80">
            <tr class="border-b border-line align-top">
              <td class="py-3 pr-4 font-medium text-ink">Selling Commission</td>
              <td class="py-3 pr-4">8% of total item value (standard)</td>
              <td class="py-3 pr-4">Seller</td>
              <td class="py-3">
                Deducted from the final payout to the seller upon successful completion of the sale.
                Galopa may offer an individual seller a reduced rate — via a promotional code or an
                arrangement negotiated directly with Galopa — for one sale, a set number of sales, or
                on an ongoing basis, at Galopa's discretion.
              </td>
            </tr>
            <tr class="border-b border-line align-top">
              <td class="py-3 pr-4 font-medium text-ink">Buyer Service Fee</td>
              <td class="py-3 pr-4">3% of total item value</td>
              <td class="py-3 pr-4">Buyer</td>
              <td class="py-3">
                Added at checkout on every order regardless of delivery method. Fixed — never
                discounted.
              </td>
            </tr>
            <tr class="border-b border-line align-top">
              <td class="py-3 pr-4 font-medium text-ink">Shipping Insurance</td>
              <td class="py-3 pr-4">1% of total item value</td>
              <td class="py-3 pr-4">Buyer</td>
              <td class="py-3">
                Only charged when the buyer selects delivery instead of local pickup.
              </td>
            </tr>
            <tr class="align-top">
              <td class="py-3 pr-4 font-medium text-ink">Delivery Fee</td>
              <td class="py-3 pr-4">Calculated live via Australia Post at checkout</td>
              <td class="py-3 pr-4">Buyer</td>
              <td class="py-3">
                Only charged when the buyer selects delivery. Not charged for local pickup.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="mt-4 text-base leading-relaxed text-ink/80">
        If a listing offers local pickup and the buyer selects it, neither the shipping insurance nor
        the delivery fee apply — only the 3% buyer service fee is charged.
      </p>
      <p class="mt-4 text-base leading-relaxed text-ink/80">
        <span class="font-medium text-ink">Free resell:</span> If a buyer completes a purchase and the
        saddle turns out not to suit their horse — while otherwise matching how it was listed — they
        keep the saddle and are not entitled to a refund. Instead, Galopa will waive the standard
        selling commission on that buyer's own next listing of that same saddle, so they can resell it
        on the platform at 0% commission. This is applied by Galopa on a case-by-case basis and is not
        an automatic feature of your account.
      </p>
      <p class="mt-4 text-base leading-relaxed text-ink/80">
        <span class="font-medium text-ink">Seller responsibility for delivery orders:</span> the buyer
        has already paid for Australia Post's <span class="italic">Extra Cover</span> (for the full
        sale value) and <span class="italic">Signature on Delivery</span> as part of your payout, but
        Galopa cannot add these on your behalf. You must select both yourself when you lodge the
        parcel — Australia Post does not apply them automatically.
      </p>
    </section>

    <section>
      <h2 class="font-display text-2xl font-semibold text-ink">3. Listing Rules and Accuracy</h2>
      <p class="mt-3 text-base leading-relaxed text-ink/80">
        Sellers must ensure all listings are veridical and highly accurate.
      </p>
      <ul class="mt-4 list-disc space-y-2 pl-5 text-base leading-relaxed text-ink/80">
        <li>
          The physical condition of the saddle must exactly match the photos and written description
          provided.
        </li>
        <li>Any existing damage, wear, or alterations must be explicitly documented.</li>
        <li>
          Galopa reserves the right to immediately unpublish products that do not follow our
          <a routerLink="/terms" fragment="publication-rules" class="text-forest underline underline-offset-2 hover:text-forestdark"
            >publication rules</a
          >, appear fraudulent, or lack sufficient detail.
        </li>
      </ul>
    </section>

    <section>
      <h2 class="font-display text-2xl font-semibold text-ink">4. Communication and Privacy Pre-Sale</h2>
      <p class="mt-3 text-base leading-relaxed text-ink/80">
        To protect our users and prevent off-platform transactions, strict communication rules apply.
      </p>
      <ul class="mt-4 list-disc space-y-2 pl-5 text-base leading-relaxed text-ink/80">
        <li>
          Until a formal offer is made and accepted through the platform, users may not share
          Personally Identifiable Information (PII).
        </li>
        <li>
          Prohibited information includes email addresses, phone numbers, physical addresses, or
          links to external websites.
        </li>
        <li>Violating this rule will result in immediate account suspension.</li>
      </ul>
    </section>

    <section>
      <h2 class="font-display text-2xl font-semibold text-ink">5. Payments and the Inspection Window</h2>
      <p class="mt-3 text-base leading-relaxed text-ink/80">
        To protect both buyers and sellers, payments are processed through Stripe and held securely by
        Galopa for the duration of the 48-hour inspection window.
      </p>
      <ul class="mt-4 list-disc space-y-2 pl-5 text-base leading-relaxed text-ink/80">
        <li>
          <span class="font-medium text-ink">Authorisation:</span> When a buyer makes an offer or
          buy-now request, they provide a payment method that is authorised, but not charged, while
          the request is pending.
        </li>
        <li>
          <span class="font-medium text-ink">Capture:</span> If the seller accepts, the buyer's card is
          charged in full immediately and the funds are held securely by Galopa. If the seller
          declines, doesn't respond within 48 hours, or the buyer cancels first, the authorisation is
          released and the buyer is never charged.
        </li>
        <li>
          <span class="font-medium text-ink">Inspection window start:</span> The 48-hour inspection
          window does not begin at acceptance. It begins once the seller has confirmed the saddle was
          shipped or handed over <span class="italic">and</span> the buyer has confirmed receiving it —
          both confirmations are required.
        </li>
        <li>
          <span class="font-medium text-ink">Inspection window end:</span> The buyer may confirm the
          saddle is as described at any point within the 48 hours, which finalises the sale
          immediately. If the buyer takes no action, the sale automatically finalises at the end of the
          48-hour window and both parties are notified. Alternatively, if the saddle is not as
          described, the buyer may report an issue and request a refund within the 48 hours instead —
          see Section 6.
        </li>
        <li>
          Once a sale finalises (by buyer confirmation or automatically at the end of the 48-hour
          window), the funds — minus the seller's commission — become payable to the seller. The
          seller is only asked to confirm anything further if the buyer instead reports an issue and
          requests a refund, in which case the seller confirms the returned saddle's condition (Section
          6).
        </li>
      </ul>
    </section>

    <section>
      <h2 class="font-display text-2xl font-semibold text-ink">6. Refunds and Disputes</h2>
      <p class="mt-3 text-base leading-relaxed text-ink/80">
        Refunds are available only when a saddle is not as described — damaged, inauthentic, or
        materially different from the listing — not simply because it does not suit the buyer's horse.
        If a saddle is exactly as described but is not the right fit, the buyer keeps it; see Section 2
        for how Galopa supports reselling it on the platform at 0% commission instead.
      </p>
      <ul class="mt-4 list-disc space-y-2 pl-5 text-base leading-relaxed text-ink/80">
        <li>
          <span class="font-medium text-ink">Reporting an Issue:</span> The buyer may report an issue
          and request a refund at any point during the 48-hour inspection window if the saddle received
          does not match its listing.
        </li>
        <li>
          <span class="font-medium text-ink">Return Shipping:</span> If a refund is approved because
          the saddle is not as described, the buyer arranges and pays for return shipping themselves —
          or returns it in person for a local-pickup order.
        </li>
        <li>
          <span class="font-medium text-ink">The Refund:</span> Once the seller receives the returned
          saddle and confirms it is in the identical condition in which it was sent, the buyer is
          refunded everything they were charged — the item price, the buyer service fee, and (for a
          delivery order) the shipping insurance and delivery fee. The only cost the buyer does not
          recover is what they pay to ship the saddle back.
        </li>
        <li>
          <span class="font-medium text-ink">If the Seller Disputes the Return:</span> If the seller
          believes the saddle was damaged during the inspection window rather than returned in its
          original condition, this is handled as a dispute — see Section 7.
        </li>
      </ul>
    </section>

    <section>
      <h2 class="font-display text-2xl font-semibold text-ink">7. Managing Disputes and Conflict Resolution</h2>
      <p class="mt-3 text-base leading-relaxed text-ink/80">
        Even with a clear inspection process, disputes can occur on high-value items. Galopa acts as
        the final arbitrator in all platform disputes.
      </p>

      <h3 class="mt-6 text-lg font-semibold text-ink">The Evidence Mandate (Prevention)</h3>
      <ul class="mt-3 list-disc space-y-2 pl-5 text-base leading-relaxed text-ink/80">
        <li>
          <span class="font-medium text-ink">Sellers:</span> Must upload high-resolution, timestamped
          photos of the saddle (especially girth points, pommel, and cantle) immediately before
          boxing.
        </li>
        <li>
          <span class="font-medium text-ink">Buyers:</span> Must take timestamped photos immediately
          upon unboxing the saddle.
        </li>
        <li>
          Failure by either party to provide this baseline evidence automatically forfeits their
          right to win a dispute.
        </li>
      </ul>

      <h3 class="mt-6 text-lg font-semibold text-ink">Dispute Outcomes</h3>
      <p class="mt-3 text-base leading-relaxed text-ink/80">
        If funds are locked due to a disagreement, Galopa will review the unboxing/re-boxing
        evidence. Our decision regarding the release of held funds is final and binding.
      </p>
      <div class="mt-6 overflow-x-auto">
        <table class="w-full min-w-[28rem] border-collapse text-left text-sm">
          <thead>
            <tr class="border-b border-line">
              <th class="py-3 pr-4 font-semibold text-ink">Scenario</th>
              <th class="py-3 font-semibold text-ink">Galopa Resolution</th>
            </tr>
          </thead>
          <tbody class="text-ink/80">
            <tr class="border-b border-line align-top">
              <td class="py-3 pr-4 font-medium text-ink">Buyer damaged the saddle</td>
              <td class="py-3">
                Funds released to seller. Buyer keeps the saddle (forced purchase).
              </td>
            </tr>
            <tr class="border-b border-line align-top">
              <td class="py-3 pr-4 font-medium text-ink">Seller lied about condition</td>
              <td class="py-3">
                Full refund issued to buyer once return tracking shows delivery back to the seller.
              </td>
            </tr>
            <tr class="align-top">
              <td class="py-3 pr-4 font-medium text-ink">Total Deadlock / Ambiguity</td>
              <td class="py-3">
                Galopa defaults to protecting the buyer. Funds are refunded upon verified return.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="mt-4 text-base leading-relaxed text-ink/80">
        If a user rejects Galopa’s binding decision, their only recourse is to take the other user to
        a small claims tribunal (e.g., NCAT, VCAT) at their own expense. Held funds will remain
        disbursed according to Galopa’s ruling.
      </p>
    </section>

    <section>
      <h2 class="font-display text-2xl font-semibold text-ink">8. Australian Consumer Law &amp; Liability</h2>
      <p class="mt-3 text-base leading-relaxed text-ink/80">
        For peer-to-peer sales where the seller is a private individual (not a business), standard
        consumer guarantees under the Australian Consumer Law (ACL) may not apply.
      </p>
      <ul class="mt-4 list-disc space-y-2 pl-5 text-base leading-relaxed text-ink/80">
        <li>
          Galopa acts solely as a facilitator. We do not own, inspect, or guarantee the condition of
          the saddles.
        </li>
        <li>
          Galopa is not liable for indirect, incidental, or consequential damages arising from
          transactions on the platform.
        </li>
        <li>These Terms are governed by the laws of New South Wales, Australia.</li>
      </ul>
    </section>

    <section id="publication-rules" class="scroll-mt-24 border-t border-line pt-10">
      <p class="text-sm font-medium tracking-wide text-tan uppercase">Seller guidelines</p>
      <h2 class="mt-2 font-display text-3xl font-semibold text-ink italic">Publication Rules &amp; Guidelines</h2>
      <p class="mt-4 text-base leading-relaxed text-ink/80">
        Welcome to the Galopa Seller Guidelines. Our marketplace relies on trust, accuracy, and
        transparency. Because buyers have a 48-hour inspection window, your listing acts as your
        baseline evidence for the condition of your saddle.
      </p>
      <p class="mt-3 text-base leading-relaxed text-ink/80">
        Listings that do not meet these standards will be unpublished to protect the integrity of the
        platform.
      </p>

      <h3 class="mt-8 text-xl font-semibold text-ink">1. Photography Requirements (The Evidence Mandate)</h3>
      <p class="mt-3 text-base leading-relaxed text-ink/80">
        Your photos are your primary defense in the event of a dispute. All photos must be taken in a
        well-lit area (natural daylight is best) against a clean, uncluttered background. Blurry,
        dark, or heavily filtered photos are strictly prohibited.
      </p>
      <p class="mt-3 text-base leading-relaxed text-ink/80">
        To list a saddle on Galopa, you must include clear photos of the following areas:
      </p>
      <div class="mt-6 overflow-x-auto">
        <table class="w-full min-w-[28rem] border-collapse text-left text-sm">
          <thead>
            <tr class="border-b border-line">
              <th class="py-3 pr-4 font-semibold text-ink">Required Angle / Area</th>
              <th class="py-3 font-semibold text-ink">Why We Need It</th>
            </tr>
          </thead>
          <tbody class="text-ink/80">
            <tr class="border-b border-line align-top">
              <td class="py-3 pr-4 font-medium text-ink">Side Profiles (Left &amp; Right)</td>
              <td class="py-3">Shows the overall balance, flap condition, and leather quality.</td>
            </tr>
            <tr class="border-b border-line align-top">
              <td class="py-3 pr-4 font-medium text-ink">Front Profile (Pommel &amp; Gullet)</td>
              <td class="py-3">
                Demonstrates the width of the channel and condition of the pommel.
              </td>
            </tr>
            <tr class="border-b border-line align-top">
              <td class="py-3 pr-4 font-medium text-ink">Rear Profile (Cantle &amp; Channel)</td>
              <td class="py-3">
                Shows the cantle for scuffs and the width/straightness of the rear channel.
              </td>
            </tr>
            <tr class="border-b border-line align-top">
              <td class="py-3 pr-4 font-medium text-ink">Underneath (Panels)</td>
              <td class="py-3">
                Highlights the condition of the flocking/foam and any unevenness.
              </td>
            </tr>
            <tr class="border-b border-line align-top">
              <td class="py-3 pr-4 font-medium text-ink">Girth Points / Billets</td>
              <td class="py-3">Proves the safety and structural integrity of the straps.</td>
            </tr>
            <tr class="border-b border-line align-top">
              <td class="py-3 pr-4 font-medium text-ink">Under the Flap (Stamps)</td>
              <td class="py-3">Verifies the brand, serial number, and stamped sizing.</td>
            </tr>
            <tr class="align-top">
              <td class="py-3 pr-4 font-medium text-ink">Close-ups of ANY Damage</td>
              <td class="py-3">
                Protects you from claims of undisclosed scratches, tears, or fading.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="mt-4 text-base leading-relaxed text-ink/80">
        <span class="font-medium text-ink">Crucial Reminder:</span> In addition to your listing
        photos, you are required under our Terms of Use to take high-resolution, timestamped photos
        of the saddle immediately before packing it in the box. Keep these on your phone. If a buyer
        claims the saddle arrived damaged, this pre-boxing evidence is the only way you will win the
        dispute.
      </p>

      <h3 class="mt-8 text-xl font-semibold text-ink">2. Writing the Perfect Description</h3>
      <p class="mt-3 text-base leading-relaxed text-ink/80">
        A veridical and accurate description prevents disputed refund claims. Buyers need to know
        exactly what they are paying for. Your description must include the following details:
      </p>
      <ul class="mt-4 list-disc space-y-2 pl-5 text-base leading-relaxed text-ink/80">
        <li>
          <span class="font-medium text-ink">Brand and Model:</span> (e.g., Bates Isabell, CWD
          Mademoiselle).
        </li>
        <li>
          <span class="font-medium text-ink">Discipline:</span> (e.g., Dressage, Jump, All-Purpose,
          Western).
        </li>
        <li>
          <span class="font-medium text-ink">Size:</span> Seat size (e.g., 17.5") and Gullet width
          (e.g., Medium-Wide, or state if it has an interchangeable gullet system).
        </li>
        <li>
          <span class="font-medium text-ink">Flocking Type:</span> Wool, foam, or air (CAIR). State
          when it was last professionally re-flocked or checked by a saddle fitter, if known.
        </li>
        <li>
          <span class="font-medium text-ink">Age:</span> Approximate year of manufacture (often found
          in the serial number).
        </li>
      </ul>

      <h3 class="mt-8 text-xl font-semibold text-ink">3. The “No Surprises” Damage Disclosure</h3>
      <p class="mt-3 text-base leading-relaxed text-ink/80">
        You must explicitly list every single flaw, no matter how minor. Normal wear and tear on a
        second-hand saddle is expected, but surprises are what lead to failed inspections and return
        shipping disputes.
      </p>
      <p class="mt-3 text-base leading-relaxed text-ink/80">Please explicitly state if the saddle has:</p>
      <ul class="mt-4 list-disc space-y-2 pl-5 text-base leading-relaxed text-ink/80">
        <li>Scratches on the cantle from boots/mounting.</li>
        <li>Fading or discoloration on the seat or flaps.</li>
        <li>Stretched or worn girth points.</li>
        <li>Any previous repairs (e.g., replaced billets, patched leather).</li>
      </ul>

      <h3 class="mt-8 text-xl font-semibold text-ink">4. Strict Privacy Rules (No PII)</h3>
      <p class="mt-3 text-base leading-relaxed text-ink/80">
        To ensure a secure transaction, you may not include any Personal Identifiable Information (PII)
        in your listing.
      </p>
      <ul class="mt-4 list-disc space-y-2 pl-5 text-base leading-relaxed text-ink/80">
        <li>Do not put your phone number or email address in the description.</li>
        <li>
          Do not upload photos that show your home address, vehicle license plates, or contact
          details in the background.
        </li>
        <li>
          Do not ask buyers to message you on external platforms (like Facebook or Instagram).
        </li>
      </ul>
      <p class="mt-4 text-base leading-relaxed text-ink/80">
        Listings containing PII will be immediately removed, and repeat offenders will have their
        accounts suspended.
      </p>

      <h3 class="mt-8 text-xl font-semibold text-ink">Ready to Publish?</h3>
      <p class="mt-3 text-base leading-relaxed text-ink/80">
        Before you hit “List,” double-check that your saddle looks exactly in person as it does on
        screen. Complete, honest listings sell faster and result in smooth, dispute-free inspections.
      </p>
    </section>
  </div>

  <p class="mt-12 border-t border-line pt-8 text-sm text-ink/60">
    See also our
    <a routerLink="/privacy" class="text-forest underline underline-offset-2 hover:text-forestdark"
      >Privacy Policy</a
    >.
  </p>
</article>
